import type { ExchangeCurrencyCode, ExchangeRate } from '@/services/dto';
import type { TFunction } from 'i18next';

export const CURRENCY_DISPLAY_ORDER: ExchangeCurrencyCode[] = ['KRW', 'JPY', 'EUR', 'CNY'];

export const CURRENCY_SYMBOL_MAP: Record<ExchangeCurrencyCode, string> = {
    KRW: '₩',
    JPY: '¥',
    EUR: '€',
    CNY: '元',
};

export const EXCHANGE_RATE_AUTO_ROTATE_MS = 5000;

export const sortExchangeRates = (rates: ExchangeRate[] | null | undefined): ExchangeRate[] => {
    if (!Array.isArray(rates) || rates.length === 0) {
        return [];
    }

    const orderMap = new Map(CURRENCY_DISPLAY_ORDER.map((code, index) => [code, index]));

    return [...rates]
        .filter((rate) => orderMap.has(rate.currencyCode))
        .sort((a, b) => (orderMap.get(a.currencyCode) ?? 0) - (orderMap.get(b.currencyCode) ?? 0));
};

export const getCurrencySymbol = (currencyCode: ExchangeCurrencyCode): string => {
    return CURRENCY_SYMBOL_MAP[currencyCode] ?? currencyCode;
};

export const formatExchangeRateValue = (value: number): string => {
    // Always show 2 decimal places, truncating instead of rounding (e.g. 1322 → "1322.00", 143.9 → "143.90", 1322.756 → "1322.75").
    // String-based to avoid float errors from Math.trunc(value * 100) (e.g. 1.15 * 100 = 114.999...).
    const [integerPart, decimalPart = ''] = String(Number(value)).split('.');
    return `${integerPart}.${decimalPart.padEnd(2, '0').slice(0, 2)}`;
};

export const getExchangeRateSpokenLabel = (rate: ExchangeRate, t: TFunction): string => {
    const label = t('home.exchange_rate.label');
    const rateSpoken = t(`home.exchange_rate.tts.rate.${rate.currencyCode}`, {
        rate: formatExchangeRateValue(rate.exchangeRate),
    });

    return `${label}. ${rateSpoken}`;
};
